/**
 * useBotSafetyTimeout — client-side robustness for the async bot loop.
 *
 * Context (perf-plan PR E.3):
 * After PR F ("fix/decouple-bot-loop") the server returns the HTTP 200
 * immediately after the human's action, with the game now on a bot's
 * turn. The bots then play asynchronously, emitting GAME_STATE_PATCH
 * events for animation and a final GAME_UPDATED event when it's the
 * next human's turn. The WS path is an optimization — NOT a dependency.
 *
 * Risk: on iOS cellular (or any dropped/reconnected WS), the final
 * GAME_UPDATED may never arrive, leaving the screen permanently showing
 * "bot's turn" with no action available.
 *
 * This hook arms a ~30s safety timeout whenever:
 *   1. The game is ACTIVE.
 *   2. The current player (by index) is a bot (`isBot === true`).
 *
 * When the timeout fires without a terminal signal having arrived (i.e.
 * the bot's turn still shows in the cache), it does an authoritative HTTP
 * refetch via `queryClient.invalidateQueries`. If the cache *still* shows a
 * bot's turn after the refetch resolves, the `onStuck` callback is invoked
 * so the screen can surface a non-fatal toast.
 *
 * Idempotency: the timeout is reset on every render that sees a *different*
 * `currentPlayerIndex`, so duplicate GAME_UPDATED events (the new server
 * emits two per bot-following turn) are harmless — the second event moves
 * `currentPlayerIndex` to its already-current value, a new effect run sees
 * the same dependency value, and React skips the effect cleanup/re-arm.
 *
 * Safe against both server generations:
 *   - Current prod: HTTP 200 already returns post-bot state (human turn).
 *     `isBot` will be false right after the mutation, so this hook never
 *     arms the timeout at all.
 *   - New server (PR F): HTTP 200 returns post-HUMAN state (bot's turn).
 *     `isBot` is true → timeout is armed → WS patch arrives within seconds
 *     → effect dependency changes → timeout is cancelled. No stuck client.
 */

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { GameResponse } from '@warcarp/api-types'
import { currentPlayer } from '../lib/turnHelpers'

/** Time to wait (ms) before treating silence as a stuck bot loop. */
const BOT_TURN_SAFETY_TIMEOUT_MS = 30_000

/**
 * Returns true when the game is ACTIVE and the current turn belongs to a bot
 * player — i.e. we are in the "waiting for bots to finish" window.
 */
export function isOnBotTurn(game: GameResponse | undefined | null): boolean {
  if (!game || game.status !== 'ACTIVE') return false
  return currentPlayer(game)?.isBot === true
}

export interface BotSafetyTimeoutOptions {
  /**
   * Called when the safety timeout fires AND an authoritative refetch still
   * shows a bot's turn. Use this to surface a non-fatal toast to the user.
   * The hook already performed the `invalidateQueries` refetch — the caller
   * only needs to decide on the UX reaction (e.g. "Taking longer than usual…").
   */
  onStuck?: () => void
}

/**
 * Arms a safety timeout when `game` shows it's currently a bot's turn.
 * The timeout triggers an authoritative HTTP refetch; if the game is still
 * stuck on a bot's turn after the refetch it calls `options.onStuck`.
 *
 * @param gameId   The game ID — used to build the React Query key.
 * @param game     The current cached GameResponse (may be undefined while loading).
 * @param options  Optional callbacks.
 */
export function useBotSafetyTimeout(
  gameId: string | undefined,
  game: GameResponse | undefined | null,
  options?: BotSafetyTimeoutOptions,
): void {
  const qc = useQueryClient()
  // Keep a stable ref to the callback so the effect closure doesn't go stale.
  const onStuckRef = useRef(options?.onStuck)
  onStuckRef.current = options?.onStuck

  // Derived: is the current turn owned by a bot?
  const botTurn = isOnBotTurn(game)

  // We depend on `currentPlayerIndex` as the "turn has changed" signal.
  // When the WS delivers GAME_UPDATED (bot→human transition), currentPlayerIndex
  // changes, the effect re-runs, the old timeout is cancelled, and no new
  // timeout is armed (botTurn === false). Duplicate GAME_UPDATED events that
  // carry the same index are harmless — the effect skips cleanup/re-arm.
  const currentPlayerIndex = game?.currentPlayerIndex ?? -1

  // Forward-progress signal. A bot mid-turn emits GAME_STATE_PATCH events that
  // grow the event log (and bump `eventCount`) WITHOUT advancing
  // `currentPlayerIndex`. Depending on this re-arms the watchdog on every bot
  // step, so a long-but-healthy bot turn (or a sequence of bots) never trips
  // the 30s timer — only genuine silence does. Falls back to the inline events
  // length when `eventCount` is absent (older payloads / pre-trim wire).
  const progress = botTurnProgress(game)

  useEffect(() => {
    // Only arm when we have a game ID and the turn is genuinely on a bot.
    if (!gameId || !botTurn) return

    const timer = setTimeout(async () => {
      // Timeout fired — no terminal WS event arrived. Force an authoritative
      // refetch and WAIT for the fresh result before judging. (The old code
      // read the cache synchronously after `invalidateQueries`, i.e. BEFORE the
      // refetch resolved, so it surfaced the "taking longer" toast even when the
      // refetch was about to show the turn had already moved on — a dropped WS
      // event that the refetch silently recovers. That false alarm is exactly
      // what we're fixing here.)
      try {
        await qc.refetchQueries({ queryKey: ['games', gameId], exact: true })
      } catch {
        // Network error — fall through and judge on whatever the cache holds.
      }

      // Only surface the toast if the game is GENUINELY still stuck on a bot's
      // turn AFTER the authoritative refetch. A recoverable dropped-WS case
      // heals here silently with no user-facing noise.
      const current = qc.getQueryData<GameResponse>(['games', gameId])
      if (isOnBotTurn(current)) {
        onStuckRef.current?.()
      }
    }, BOT_TURN_SAFETY_TIMEOUT_MS)

    return () => clearTimeout(timer)

    // Re-arm when bot-turn status, the active player, OR bot forward-progress
    // changes (see `progress` above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, botTurn, currentPlayerIndex, progress, qc])
}

/**
 * Monotonic "the bot loop is making progress" counter for a game: the true
 * total event count when present (`state.eventCount`), else the inline events
 * length. Used by the safety timeout to distinguish a healthy, progressing bot
 * turn (counter keeps rising) from a genuinely stuck one (counter frozen).
 * Pure + exported for testing.
 */
export function botTurnProgress(game: GameResponse | undefined | null): number {
  const state = game?.state as
    | { eventCount?: number; events?: unknown[] }
    | null
    | undefined
  return state?.eventCount ?? state?.events?.length ?? 0
}
